import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideEye, lucideEyeOff, lucideLock, lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputGroupImports } from '@spartan-ng/helm/input-group';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { Subject, takeUntil, filter } from 'rxjs';
import { CompanyService, Company } from '../../../services/company.service';
import { LoggerService } from '../../../services/logger.service';
import { SaveButtonComponent } from '../../save-button/save-button.component';

/**
 * Redemption PIN for staff, plus the unblock action after failed attempts.
 * Saves independently from the fee card above it — group members only ever see
 * this one.
 */
@Component({
  selector: 'app-redemption-pin-settings',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SaveButtonComponent,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputGroupImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideEye, lucideEyeOff, lucideLock, lucideTriangleAlert })],
  // Inline hosts ignore the vertical margin of the page's `space-y`.
  host: { class: 'block' },
  templateUrl: './redemption-pin-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class RedemptionPinSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private initialPin = '';

  loading = false;
  formChanged = false;
  unblocking = false;
  unblockSuccess = false;
  unblockError = '';
  showPin = false;
  company: Company | null = null;

  @ViewChild('saveButton') saveButton!: SaveButtonComponent;

  pinForm: FormGroup;

  constructor(
    private companyService: CompanyService,
    private fb: FormBuilder,
    private logger: LoggerService,
  ) {
    this.pinForm = this.fb.group({
      redemption_pin: ['', [Validators.pattern(/^\d{4,6}$/)]],
    });

    this.pinForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChanged = this.pinForm.value.redemption_pin !== this.initialPin;
    });
  }

  ngOnInit() {
    this.companyService.company$
      .pipe(
        takeUntil(this.destroy$),
        filter((c) => c !== null),
      )
      .subscribe((company) => {
        this.company = company;
        this.populateForm(company);
      });
  }

  private populateForm(company: Company) {
    this.initialPin = company.redemption_pin || '';
    this.pinForm.patchValue({ redemption_pin: this.initialPin }, { emitEvent: false });
    this.formChanged = false;
  }

  onSubmit() {
    if (!this.pinForm.valid) return;
    this.loading = true;
    this.saveButton.hideMessages();

    // Only the PIN is sent, so saving here cannot overwrite the fee setting.
    this.companyService.updateCompanyDetails(this.pinForm.value).subscribe({
      next: () => {
        this.loading = false;
        this.initialPin = this.pinForm.value.redemption_pin;
        this.formChanged = false;
        this.saveButton.showSuccessMessage();
      },
      error: (error) => {
        const message = error.error?.detail || 'Fehler beim Speichern. Bitte versuche es erneut.';
        this.logger.error('Error saving redemption PIN:', error);
        this.loading = false;
        this.saveButton.showErrorMessage(message);
      },
    });
  }

  get pinInvalid(): boolean {
    const control = this.pinForm.get('redemption_pin');
    return !!control?.invalid && !!control?.touched;
  }

  togglePinVisibility() {
    this.showPin = !this.showPin;
  }

  unblockRedemption() {
    if (this.unblocking) return;
    this.unblocking = true;
    this.unblockError = '';
    this.unblockSuccess = false;

    this.companyService.unblockPublicRedemption().subscribe({
      next: () => {
        this.unblocking = false;
        this.unblockSuccess = true;
        setTimeout(() => {
          this.unblockSuccess = false;
        }, 5000);
      },
      error: (error) => {
        this.logger.error('Fehler beim Entsperren:', error);
        this.unblockError =
          error.error?.detail || 'Fehler beim Entsperren. Bitte versuche es erneut.';
        this.unblocking = false;
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
